/**
 * Multiplayer Game — responsive layout.
 *
 * Portrait (mobile):
 * ┌──────────────────────────┐
 * │ Title bar (44px)         │
 * ├──────────────────────────┤
 * │       ○ Board            │
 * │       ○ Controller       │
 * ├──────────────────────────┤
 * │    [ Action bar ] (56px) │
 * └──────────────────────────┘
 *
 * Landscape (tablet):
 * ┌──────────────────────────────────┐
 * │ Title bar                        │
 * ├──────────────┬───────────────────┤
 * │   ○ Board    │  ○ Controller     │
 * │              │  [ Confirm ]      │
 * ├──────────────┴───────────────────┤
 * │         Action bar               │
 * └──────────────────────────────────┘
 *
 * Desktop:
 * ┌──────────────────────────────────────────┐
 * │ Title bar                                │
 * ├──────────┬──────────────┬────────────────┤
 * │ GameInfo │   ○ Board    │ ○ Controller   │
 * │ (side)   │              │ [ Confirm ]    │
 * ├──────────┴──────────────┴────────────────┤
 * │              Action bar                  │
 * └──────────────────────────────────────────┘
 */

import type { PlayerId } from "@cloudnest/augmented-stone-flicking-engine";
import { countRemaining } from "@cloudnest/stone-flicking-core-engine/utils";
import "./asf-animations.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildLobbyPath,
  navigateTo,
  type RouteMode,
  useRoute,
} from "../../router";
import {
  EmojiMessagePanel,
  emojiMessageActions,
  handleEmojiEvent,
  resetEmojiEventState,
} from "../../shared/emoji-message";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { createGameApi } from "../../shared/services/game-api";
import { getUserLabel, useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { FlickController } from "./components/FlickController";
import { RoundTimer } from "./components/RoundTimer";
import { GameCanvas } from "./GameCanvas";
import { GameInfo } from "./GameInfo";
import { GameOverScreen } from "./GameOverScreen";
import { useCanvasSize } from "./hooks/use-canvas-size";
import type { ControllerDragState } from "./hooks/use-flick-controller";
import {
  multiplayerGameActions,
  useMultiplayerGame,
  useMultiplayerGameActions,
} from "./multiplayer-game-store";

export function MultiplayerGame() {
  const { user } = useAuth();
  const route = useRoute();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const mpState = useMultiplayerGame();
  const mpActions = useMultiplayerGameActions();
  const roomId = currentRoom?.id ?? null;
  const userLabel = getUserLabel(user);
  const routeMode: RouteMode =
    route.name === "game" && route.routeMode === "v2" ? "v2" : "legacy";
  const asfGameApi = useMemo(
    () =>
      createGameApi(
        "augmented-stone-flicking",
        routeMode === "v2" ? "game-platform" : "legacy",
      ),
    [routeMode],
  );

  const isHost = currentRoom?.hostUserId === user?.id;
  const isGameOver = mpState.gameState?.phase === "GAME_END";
  const isConfirmed = mpState.isWaitingForOthers;
  const canInteract =
    !isGameOver &&
    !isConfirmed &&
    !mpState.isAnimating &&
    mpState.gameState?.phase === "PLAYING";

  const canvasSize = useCanvasSize();
  const controllerSize = canvasSize.controllerSize;
  const layoutMode = canvasSize.layoutMode;
  const isWideLayout = layoutMode === "landscape" || layoutMode === "desktop";

  const [controllerDrag, setControllerDrag] =
    useState<ControllerDragState | null>(null);

  const externalFlickPreview = controllerDrag?.isDragging
    ? { dx: controllerDrag.dx, dy: controllerDrag.dy }
    : null;

  const mpActionsRef = useRef(mpActions);
  const lobbyActionsRef = useRef(lobbyActions);
  useEffect(() => {
    mpActionsRef.current = mpActions;
    lobbyActionsRef.current = lobbyActions;
  }, [mpActions, lobbyActions]);

  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: (eventType, payload, seq, userId) => {
      if (handleEmojiEvent(eventType, payload, userId)) return;
      mpActionsRef.current.applyRemoteEvent(eventType, payload, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void multiplayerGameActions.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: using primitive deps
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;
    const hostFlag = currentRoom.hostUserId === user.id;
    const PLAYER_IDS: ReadonlyArray<PlayerId> = ["P1", "P2", "P3", "P4"];
    const playerIndex = currentRoom.participantUserIds.indexOf(user.id);
    const assignedPlayerId: PlayerId =
      (playerIndex >= 0 && playerIndex < PLAYER_IDS.length
        ? PLAYER_IDS[playerIndex]
        : undefined) ?? "P1";

    if (hostFlag) {
      void multiplayerGameActions.initializeAsHost(roomId, assignedPlayerId);
    } else {
      multiplayerGameActions.initializeAsGuest(roomId, assignedPlayerId);
      void multiplayerGameActions.replayEvents(roomId);
    }
  }, [roomId, currentRoom?.id, user?.id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — only re-run on submission count change
  useEffect(() => {
    if (!isHost || !mpState.gameState) return;
    if (mpState.gameState.phase !== "PLAYING") return;
    const activeCount =
      mpState.gameState.coreState.participants.length -
      mpState.gameState.coreState.eliminatedParticipants.length;
    const submittedCount =
      mpState.gameState.coreState.submittedParticipants.length;
    if (submittedCount >= activeCount && activeCount > 0) {
      void multiplayerGameActions.resolveRound();
    }
  }, [isHost, mpState.gameState?.coreState.submittedParticipants.length]);

  const handleFlick = useCallback(
    async (entityId: string, impulseX: number, impulseY: number) => {
      if (!canInteract || !mpState.myPlayerId) return;
      await mpActions.confirmInput(entityId, { x: impulseX, y: impulseY });
    },
    [canInteract, mpState.myPlayerId, mpActions],
  );

  const handleControllerFlick = useCallback(
    async (impulseX: number, impulseY: number) => {
      if (!canInteract || !mpState.myPlayerId || !mpState.selectedEntityId)
        return;
      await mpActions.confirmInput(mpState.selectedEntityId, {
        x: impulseX,
        y: impulseY,
      });
    },
    [canInteract, mpState.myPlayerId, mpState.selectedEntityId, mpActions],
  );

  const handleAnimationComplete = useCallback(() => {
    mpActions.finishAnimation();
  }, [mpActions]);

  const handleTimeout = useCallback(async () => {
    await mpActions.handleTimeout();
  }, [mpActions]);

  const handleSurrender = useCallback(async () => {
    await mpActions.submitSurrender();
  }, [mpActions]);

  const handleLeaveGame = useCallback(() => {
    emojiMessageActions.clearAll();
    resetEmojiEventState();
    mpActions.reset();
    lobbyActions.leaveGame();
    navigateTo(buildLobbyPath("augmented-stone-flicking", routeMode));
  }, [mpActions, lobbyActions, routeMode]);

  // ─── Loading state ───
  if (!currentRoom || !mpState.gameState) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100dvh",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          backgroundColor: "var(--cn-asf-surface-secondary)",
          color: "var(--cn-asf-text)",
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            border: "3px solid rgba(0,0,0,0.1)",
            borderTopColor: "var(--cn-asf-stone-p1)",
            borderRadius: "50%",
            animation: "asf-spin 0.8s linear infinite",
          }}
        />
        <span style={{ color: "var(--cn-asf-text-muted)", fontSize: "14px" }}>
          불러오는 중...
        </span>
      </div>
    );
  }

  const spaceConfig = mpState.config.board;
  const playerCount = mpState.gameState.playerCount;
  const round = mpState.gameState.round;
  const showTimer = !isGameOver && mpState.gameState.phase === "PLAYING";
  const canConfirm = canInteract && !!mpState.selectedEntityId;

  const titleText = isGameOver
    ? mpState.gameState.isDraw
      ? "무승부"
      : mpState.gameState.winner === mpState.myPlayerId
        ? "승리!"
        : "패배"
    : isConfirmed
      ? `라운드 ${round} · 대기 중`
      : mpState.isAnimating
        ? `라운드 ${round} · 실행 중`
        : `라운드 ${round} · 입력`;

  const confirmButton = (
    <button
      data-testid="confirm-input-btn"
      type="button"
      disabled={!canConfirm}
      onClick={() => {
        if (controllerDrag?.isDragging) return;
        if (canConfirm) void handleControllerFlick(0, 0);
      }}
      aria-label={isConfirmed ? "대기 중" : "입력 확인"}
      style={{
        padding: "12px 32px",
        fontSize: "14px",
        fontWeight: 700,
        border: "none",
        borderRadius: "24px",
        cursor: canConfirm ? "pointer" : "default",
        backgroundColor: canConfirm
          ? "var(--cn-asf-stone-p1)"
          : "var(--cn-asf-surface-secondary)",
        color: "#fff",
        opacity: canConfirm ? 1 : 0.5,
        transition: "opacity 0.2s, background-color 0.2s, transform 0.15s",
        minHeight: "48px",
        minWidth: "120px",
        transform: canConfirm ? "scale(1)" : "scale(0.97)",
      }}
    >
      {isConfirmed ? "대기 중..." : "입력 확인"}
    </button>
  );

  const leaveButton = (
    <button
      data-testid="game-over-leave-btn"
      type="button"
      onClick={handleLeaveGame}
      style={{
        padding: "12px 32px",
        fontSize: "14px",
        fontWeight: 700,
        border: "none",
        borderRadius: "24px",
        cursor: "pointer",
        backgroundColor: "var(--cn-asf-stone-p1)",
        color: "#fff",
        minHeight: "48px",
        minWidth: "120px",
      }}
    >
      나가기
    </button>
  );

  const canvasElement = (
    <GameCanvas
      entities={mpState.gameState.coreState.entities}
      selectedEntityId={mpState.selectedEntityId}
      isAnimating={mpState.isAnimating}
      animationRecording={mpState.animationRecording}
      myPlayerId={mpState.myPlayerId}
      playerCount={playerCount}
      readyPlayers={mpState.readyPlayers as ReadonlyArray<string>}
      spaceConfig={spaceConfig}
      canInteract={canInteract}
      canvasWidth={canvasSize.width}
      canvasHeight={canvasSize.height}
      canvasPadding={canvasSize.padding}
      externalFlickPreview={externalFlickPreview}
      onSelectEntity={mpActions.selectEntity}
      onFlick={handleFlick}
      onAnimationComplete={handleAnimationComplete}
    />
  );

  const controllerElement = (
    <FlickController
      size={controllerSize}
      isEnabled={canInteract}
      selectedPieceId={mpState.selectedEntityId}
      onFlick={handleControllerFlick}
      onDragStateChange={setControllerDrag}
    />
  );

  const PLAYER_COLOR_TOKEN: Record<PlayerId, string> = {
    P1: "var(--cn-asf-stone-p1)",
    P2: "var(--cn-asf-stone-p2)",
    P3: "var(--cn-asf-stone-p3)",
    P4: "var(--cn-asf-stone-p4)",
  };
  const PLAYER_BG_TOKEN: Record<PlayerId, string> = {
    P1: "var(--cn-asf-stone-p1-bg)",
    P2: "var(--cn-asf-stone-p2-bg)",
    P3: "var(--cn-asf-stone-p3-bg)",
    P4: "var(--cn-asf-stone-p4-bg)",
  };
  const PLAYER_IDS_BY_INDEX: ReadonlyArray<PlayerId> = ["P1", "P2", "P3", "P4"];
  const activePlayers = PLAYER_IDS_BY_INDEX.slice(
    0,
    mpState.gameState.playerCount,
  );
  // ASF is simultaneous-turn, so the "active" highlight follows the local
  // player's seat — that's where the chrome attention should live.
  const currentPlayer: PlayerId = mpState.myPlayerId ?? "P1";

  return (
    <div
      data-testid="game-screen"
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        backgroundColor: "var(--cn-asf-surface-secondary)",
        color: "var(--cn-asf-text)",
        overflow: "hidden",
      }}
    >
      {/* ─── Title Bar ─── */}
      <header
        data-testid="asf-top-bar"
        style={{
          minHeight: "44px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 12px",
          gap: "8px",
          flexWrap: "wrap",
          borderBottom: "1px solid var(--cn-asf-surface-border)",
          flexShrink: 0,
          backgroundColor: "var(--cn-asf-surface)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={handleLeaveGame}
            data-testid="asf-new-game"
            style={{
              padding: "4px 10px",
              border: "1px solid var(--cn-asf-surface-border)",
              borderRadius: "6px",
              background: "transparent",
              color: "var(--cn-asf-text)",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            ← 나가기
          </button>
          {!isGameOver && (
            <div
              data-testid="asf-active-player"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 10px",
                borderRadius: "999px",
                background: PLAYER_COLOR_TOKEN[currentPlayer],
                color: "#fff",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: "#fff",
                }}
              />
              {currentPlayer} 차례
            </div>
          )}
          <output
            aria-label={connectionStatus === "connected" ? "연결됨" : "연결 중"}
            data-testid="asf-connection-pill"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "4px 10px",
              borderRadius: "999px",
              border: "1px solid var(--cn-asf-surface-border)",
              backgroundColor: "var(--cn-asf-surface-secondary)",
              fontSize: "11px",
              color: "var(--cn-asf-text-muted)",
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor:
                  connectionStatus === "connected"
                    ? "var(--cn-asf-ready)"
                    : "var(--cn-asf-timer-warning)",
                transition: "background-color 0.3s",
              }}
            />
            {connectionStatus === "connected" ? "연결됨" : "재연결 중..."}
          </output>
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "var(--cn-asf-text)",
            }}
          >
            {titleText}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexWrap: "wrap",
          }}
        >
          {activePlayers.map((playerId) => {
            const count = countRemaining(
              mpState.gameState!.coreState.entities,
              playerId,
            );
            const isReady = mpState.readyPlayers.includes(playerId);
            const isEliminated =
              mpState.gameState!.eliminatedPlayers.includes(playerId);
            return (
              <span
                key={playerId}
                data-testid={`asf-stone-count-${playerId.toLowerCase()}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "3px 10px",
                  borderRadius: "999px",
                  background: isEliminated
                    ? "var(--cn-asf-surface-secondary)"
                    : PLAYER_BG_TOKEN[playerId],
                  color: PLAYER_COLOR_TOKEN[playerId],
                  border: `1px solid ${PLAYER_COLOR_TOKEN[playerId]}`,
                  boxShadow:
                    currentPlayer === playerId
                      ? "0 0 0 2px var(--cn-asf-stone-selected-glow)"
                      : "none",
                  opacity: isEliminated ? 0.5 : 1,
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: PLAYER_COLOR_TOKEN[playerId],
                  }}
                />
                <span className="font-mono">{count}</span>
                {isReady && (
                  <span
                    style={{ color: "var(--cn-asf-ready)", fontSize: "10px" }}
                  >
                    ✓
                  </span>
                )}
              </span>
            );
          })}
          <div
            style={{
              opacity: showTimer ? 1 : 0,
              transition: "opacity 0.2s",
            }}
          >
            <RoundTimer
              deadline={mpState.roundDeadline}
              onTimeout={handleTimeout}
              isHost={isHost}
            />
          </div>
          {!canvasSize.showSidePanel && (
            <GameInfo
              gameState={mpState.gameState}
              isAnimating={mpState.isAnimating}
              readyPlayers={mpState.readyPlayers as ReadonlyArray<string>}
              onSurrender={handleSurrender}
              onLeaveGame={handleLeaveGame}
            />
          )}
        </div>
      </header>

      {/* ─── Win banner (shown when game has ended) ─── */}
      {isGameOver && (
        <div
          data-testid="asf-win-banner"
          style={{
            padding: "8px 16px",
            backgroundColor:
              mpState.gameState.winner === mpState.myPlayerId
                ? "var(--cn-asf-stone-p1-bg)"
                : "var(--cn-asf-surface-secondary)",
            borderBottom: "1px solid var(--cn-asf-surface-border)",
            color:
              mpState.gameState.winner === mpState.myPlayerId
                ? "var(--cn-asf-stone-p1)"
                : "var(--cn-asf-text)",
            fontWeight: 700,
            textAlign: "center",
            fontSize: "14px",
            flexShrink: 0,
          }}
        >
          {mpState.gameState.isDraw
            ? "무승부"
            : mpState.gameState.winner === mpState.myPlayerId
              ? "승리!"
              : "패배"}
        </div>
      )}

      {/* ─── Error Toast ─── */}
      <div
        role="alert"
        style={{
          position: "fixed",
          top: "52px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 45,
          opacity: mpState.lastError ? 1 : 0,
          pointerEvents: mpState.lastError ? "auto" : "none",
          transition: "opacity 0.2s",
        }}
      >
        <div
          style={{
            backgroundColor: "var(--cn-asf-error)",
            color: "#fff",
            fontSize: "12px",
            padding: "8px 20px",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {mpState.lastError ?? ""}
        </div>
      </div>

      {/* ─── Main Content Area ─── */}
      {isWideLayout ? (
        /* Landscape / Desktop — side-by-side */
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "row",
            minHeight: 0,
            gap: "8px",
            padding: `0 ${canvasSize.showSidePanel ? "16px" : "8px"}`,
          }}
        >
          {/* Desktop side panel */}
          {canvasSize.showSidePanel && (
            <aside
              style={{
                width: "220px",
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                padding: "16px 0",
              }}
            >
              <GameInfo
                gameState={mpState.gameState}
                isAnimating={mpState.isAnimating}
                readyPlayers={mpState.readyPlayers as ReadonlyArray<string>}
                onSurrender={handleSurrender}
                onLeaveGame={handleLeaveGame}
              />
            </aside>
          )}

          {/* Board frame: wood-tone parchment */}
          <div
            data-testid="asf-board-frame"
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 0,
              padding: "12px",
              borderRadius: "16px",
              background: "var(--cn-asf-surface-secondary)",
              border: "8px solid var(--cn-asf-board-stroke)",
              margin: "8px 0",
            }}
          >
            {canvasElement}
          </div>

          {/* Controller + confirm */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              flexShrink: 0,
              padding: "16px 0",
            }}
          >
            {controllerElement}
            {!isGameOver && confirmButton}
            {isGameOver && leaveButton}
          </div>
        </div>
      ) : (
        /* Portrait (mobile) — stacked */
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {/* Board frame */}
          <div
            data-testid="asf-board-frame"
            style={{
              flex: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 0,
              padding: "8px",
            }}
          >
            {canvasElement}
          </div>
          {/* Controller */}
          <div
            style={{
              flex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 0,
            }}
          >
            {controllerElement}
          </div>
        </div>
      )}

      {/* ─── Action Bar (portrait only — wide layouts place buttons in controller column) ─── */}
      {layoutMode === "portrait" && (
        <footer
          style={{
            height: "56px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            padding: "0 16px",
            borderTop: "1px solid var(--cn-asf-surface-border)",
            backgroundColor: "var(--cn-asf-surface)",
            flexShrink: 0,
          }}
        >
          {!isGameOver && confirmButton}
          {isGameOver && leaveButton}
        </footer>
      )}

      {/* ─── Emoji Message System ─── */}
      {roomId && (
        <EmojiMessagePanel
          gameApi={asfGameApi}
          roomId={roomId}
          displayName={userLabel}
        />
      )}

      {/* ─── Game Over Overlay ─── */}
      {isGameOver && (
        <GameOverScreen
          gameState={mpState.gameState}
          myPlayerId={mpState.myPlayerId}
          onLeaveGame={handleLeaveGame}
        />
      )}
    </div>
  );
}
